import React, { useMemo, useId, useEffect, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  FileText,
  Calculator,
  ActivitySquare,
  Users,
  PieChart,
  Percent,
  LineChart
} from "lucide-react";

/* ---------- Small viewport hook ---------- */
const useWindowWidth = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return w;
};

/* ---------- Helpers ---------- */
const toNumber = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  return parseFloat(String(v).replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")) || 0;
};

const netFromRow = (row) =>
  toNumber(row.K_15) +
  toNumber(row.K_19) +
  toNumber(row.K_10) +
  toNumber(row.K_21) +
  toNumber(row.K_17) +
  toNumber(row.K_11) +
  toNumber(row.K_22);

const vatDueFromRow = (row) => toNumber(row.K_16) + toNumber(row.K_20) + toNumber(row.K_18);

const getYearMonth = (row) => {
  if (row?.Rok && row?.Miesiac) return `${row.Rok}-${String(row.Miesiac).padStart(2, "0")}`;
  const d = row?.DataWystawienia ? new Date(row.DataWystawienia) : null;
  if (!d || isNaN(d)) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const prevYm = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthNamePL = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString("pl-PL", { month: "short" });
};

const lastJpkDate = (rows) => {
  let latest = null;
  for (const r of rows) {
    const candidate = r?.DataWytworzeniaJPK || r?.DataWystawienia;
    if (!candidate) continue;
    const d = new Date(candidate);
    if (!isNaN(d) && (!latest || d > latest)) latest = d;
  }
  return latest;
};

const fmtPLN0 = (n) =>
  (n ?? 0).toLocaleString("pl-PL", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " zł";

const pct = (v) => (isFinite(v) ? (v * 100).toFixed(0) + "%" : "—");
const fmtPct = (v) => (isFinite(v) ? (v >= 0 ? "+" : "") + v.toFixed(1) + "%" : "—");
const deltaPct = (curr, prev) => {
  if (prev === 0) return curr > 0 ? 100 : 0;
  return ((curr - prev) / prev) * 100;
};

const topNShare = (list, n = 3) => {
  const total = list.reduce((s, x) => s + x, 0);
  if (total <= 0) return 0;
  const sorted = [...list].sort((a, b) => b - a);
  const top = sorted.slice(0, n).reduce((s, x) => s + x, 0);
  return top / total;
};

/* ---------- Sparkline (SVG area) ---------- */
const Sparkline = ({ data = [], color = "#16a34a", height = 36, opacity = 0.12, strokeOpacity = 0.8 }) => {
  const gid = useId();
  const n = data.length;
  if (!n) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const h = height;
  const pad = 2;
  const vbw = Math.max(1, n - 1);

  const y = (v) => {
    const t = (v - min) / range;
    const yy = h - pad - t * (h - pad * 2);
    return Math.max(pad, Math.min(h - pad, yy));
  };

  let line = `M 0 ${y(data[0])}`;
  for (let i = 1; i < n; i++) line += ` L ${i} ${y(data[i])}`;
  const area = `${line} L ${n - 1} ${h} L 0 ${h} Z`;

  return (
    <svg viewBox={`0 0 ${vbw} ${h}`} preserveAspectRatio="none" style={{ width: "100%", height }}>
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={opacity} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="0.12" strokeOpacity={strokeOpacity} />
    </svg>
  );
};

/* ---------- Tiny card UI ---------- */
const SectionHeader = ({ title = "Sprzedaż", icon: Icon = LineChart, compact = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      paddingBottom: compact ? 6 : 8,
    }}
  >
    <div
      style={{
        width: compact ? 30 : 36,
        height: compact ? 30 : 36,
        borderRadius: 10,
        background: "#f1f5f9",
        display: "grid",
        placeItems: "center",
      }}
    >
      <Icon size={compact ? 16 : 18} />
    </div>
    <div style={{ fontSize: compact ? "0.95rem" : "1rem", fontWeight: 800, color: "#0f172a" }}>{title}</div>
  </div>
);

const Separator = ({ compact = false }) => (
  <div style={{ height: 1, width: "100%", background: "#e5e7eb", marginBottom: compact ? 6 : 10 }} />
);

const Box = ({ children, sparkline, compact = false }) => (
  <div
    style={{
      position: "relative",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      background: "#fff",
      boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
      padding: compact ? 12 : 16,
      minHeight: compact ? 84 : 90,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      gap: compact ? 6 : 8,
      overflow: "hidden",
    }}
  >
    <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
    {sparkline && (
      <div style={{ position: "absolute", left: 8, right: 8, bottom: compact ? 6 : 8, zIndex: 0 }}>
        {sparkline}
      </div>
    )}
  </div>
);

const TitleRow = ({ icon, label, hintColor = "#64748b", compact = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      color: hintColor,
      fontSize: compact ? 11 : 12,
      fontWeight: 700,
    }}
  >
    {icon}
    <span>{label}</span>
  </div>
);

const ValueRow = ({ value, delta, color = "#111827", mm = true, compact = false }) => {
  const up = (delta ?? 0) >= 0;
  const arrow = up ? <TrendingUp size={compact ? 14 : 16} /> : <TrendingDown size={compact ? 14 : 16} />;
  const deltaColor = up ? "#16a34a" : "#dc2626";
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
      <div style={{ fontSize: compact ? 20 : 22, fontWeight: 800, color }}>{value}</div>
      {delta != null && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: deltaColor,
            fontSize: compact ? 11 : 12,
            fontWeight: 700,
          }}
        >
          {arrow}
          {fmtPct(delta)} {mm && <span style={{ color: "#94a3b8", fontWeight: 500 }}>&nbsp;m/m</span>}
        </div>
      )}
    </div>
  );
};

/* ---------- Main component (responsive) ---------- */
const SprzedazKPI = ({ salesData = [], purchasesData = [] }) => {
  const width = useWindowWidth();
  const compact = width < 520; // tweak breakpoint as you like

  const result = useMemo(() => {
    const byYm = new Map();
    salesData.forEach((r) => {
      const ym = getYearMonth(r);
      if (!ym) return;
      if (!byYm.has(ym)) byYm.set(ym, []);
      byYm.get(ym).push(r);
    });
    const yms = [...byYm.keys()].sort();
    const ymCurr = yms.length ? yms[yms.length - 1] : null;
    const ymPrev = ymCurr ? prevYm(ymCurr) : null;

    const rowsCurr = ymCurr ? byYm.get(ymCurr) || [] : [];
    const rowsPrev = ymPrev ? byYm.get(ymPrev) || [] : [];

    const monthlyNet = yms.map((ym) => (byYm.get(ym) || []).reduce((s, r) => s + netFromRow(r), 0));
    const monthlyInv = yms.map((ym) => {
      const set = new Set();
      (byYm.get(ym) || []).forEach((r) => r?.DowodSprzedazy && set.add(r.DowodSprzedazy));
      return set.size;
    });
    const monthlyVatDue = yms.map((ym) => (byYm.get(ym) || []).reduce((s, r) => s + vatDueFromRow(r), 0));

    const currNet = rowsCurr.reduce((s, r) => s + netFromRow(r), 0);
    const prevNet = rowsPrev.reduce((s, r) => s + netFromRow(r), 0);
    const netDelta = deltaPct(currNet, prevNet);

    const uniq = (arr, key) => {
      const set = new Set();
      arr.forEach((r) => r?.[key] && set.add(r[key]));
      return set.size;
    };
    const currInv = uniq(rowsCurr, "DowodSprzedazy");
    const prevInv = uniq(rowsPrev, "DowodSprzedazy");
    const invDelta = deltaPct(currInv, prevInv);

    const vatDueCurr = rowsCurr.reduce((s, r) => s + vatDueFromRow(r), 0);
    const vatDuePrev = rowsPrev.reduce((s, r) => s + vatDueFromRow(r), 0);
    const vatDueDelta = deltaPct(vatDueCurr, vatDuePrev);

    let vatInCurr = 0, vatInPrev = 0;
    if (Array.isArray(purchasesData) && purchasesData.length) {
      const byYmZ = new Map();
      purchasesData.forEach((r) => {
        const ym = getYearMonth(r);
        if (!ym) return;
        if (!byYmZ.has(ym)) byYmZ.set(ym, []);
        byYmZ.get(ym).push(r);
      });
      const rowsZCurr = ymCurr ? byYmZ.get(ymCurr) || [] : [];
      const rowsZPrev = ymPrev ? byYmZ.get(ymPrev) || [] : [];
      const vatInFromRow = (r) => toNumber(r.K_43);
      vatInCurr = rowsZCurr.reduce((s, r) => s + vatInFromRow(r), 0);
      vatInPrev = rowsZPrev.reduce((s, r) => s + vatInFromRow(r), 0);
    }

    const saldoCurr = vatDueCurr - vatInCurr;

    const asOfDate = lastJpkDate(salesData);
    const ymLabel = monthNamePL(ymCurr);

    // customers sets for current & previous month
    const currCustSet = new Set(rowsCurr.map((r) => r.NrKontrahenta || r.NazwaKontrahenta).filter(Boolean));
    const prevCustSet = new Set(rowsPrev.map((r) => r.NrKontrahenta || r.NazwaKontrahenta).filter(Boolean));

    // (existing) new customers vs prev month
    let newCustomers = 0;
    currCustSet.forEach((c) => { if (!prevCustSet.has(c)) newCustomers++; });

    // NEW: total clients this month + % vs previous month
    const currClients = currCustSet.size;
    const prevClients = prevCustSet.size;
    const clientsDelta = deltaPct(currClients, prevClients);

    let pl = 0, eu = 0, exp = 0;
    rowsCurr.forEach((r) => {
      const plPart = toNumber(r.K_15) + toNumber(r.K_17) + toNumber(r.K_19) + toNumber(r.K_10);
      const euPart = toNumber(r.K_21) + toNumber(r.K_11);
      const expPart = toNumber(r.K_22);
      pl += plPart; eu += euPart; exp += expPart;
    });
    const totalMix = pl + eu + exp || 1;
    const mix = { pl: pl / totalMix, eu: eu / totalMix, exp: exp / totalMix };

    const amountsByCustomer = new Map();
    rowsCurr.forEach((r) => {
      const key = r.NrKontrahenta || r.NazwaKontrahenta || "—";
      const val = netFromRow(r);
      amountsByCustomer.set(key, (amountsByCustomer.get(key) || 0) + val);
    });
    const top3 = topNShare([...amountsByCustomer.values()], 3);

    const slice12 = (arr) => arr.slice(-12);

    return {
      ymLabel, asOfDate,
      currNet, netDelta,
      currInv, invDelta,
      vatDueCurr, vatDueDelta,
      vatInCurr,
      saldoCurr,
      // NEW:
      currClients, clientsDelta,
      newCustomers,
      mix, top3,
      sparkNet: slice12(monthlyNet),
      sparkInv: slice12(monthlyInv),
      sparkVatDue: slice12(monthlyVatDue),
    };
  }, [salesData, purchasesData]);

  const {
    ymLabel, asOfDate,
    currNet, netDelta,
    currInv, invDelta,
    vatDueCurr, vatDueDelta, vatInCurr,
    saldoCurr,
    // NEW:
    currClients, clientsDelta,
    newCustomers, mix, top3,
    sparkNet, sparkInv, sparkVatDue
  } = result;

  const greenOrRed = (d) => (d >= 0 ? "#16a34a" : "#dc2626");
  const blue = "#2563eb";
  const black = "#111827";

const asOfText =
  asOfDate && ymLabel
    ? `Stan na miesiąc ${ymLabel} ${asOfDate.getFullYear()}, na podstawie ostatniego pliku JPK`
    : `Stan na miesiąc — , na podstawie ostatniego pliku JPK`;

  /* Responsive grid: auto-wrap tiles */
  const gridStyle = {
    display: "grid",
    gap: 12,
    gridTemplateColumns: width >= 1400 ? "repeat(4, minmax(240px, 1fr))" : "repeat(auto-fit, minmax(240px, 1fr))",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header + separator */}
      <SectionHeader title="Sprzedaż" icon={LineChart} compact={compact} />
      <Separator compact={compact} />

      {/* KPI grid (responsive) */}
      <div style={gridStyle}>
        {/* 1 */}
        <Box compact={compact} sparkline={sparkNet?.length ? <Sparkline data={sparkNet} color="#16a34a" height={compact ? 28 : 32} /> : null}>
          <TitleRow compact={compact} icon={<BarChart3 size={compact ? 12 : 14} />} label="Sprzedaż netto (miesiąc)" />
          <ValueRow compact={compact} value={fmtPLN0(currNet)} delta={netDelta} color={greenOrRed(netDelta ?? 0)} />
        </Box>

        {/* 2 */}
        <Box compact={compact} sparkline={sparkInv?.length ? <Sparkline data={sparkInv} color="#2563eb" height={compact ? 28 : 32} /> : null}>
          <TitleRow compact={compact} icon={<FileText size={compact ? 12 : 14} />} label="Faktury sprzedaży – ilość" hintColor={blue} />
          <ValueRow compact={compact} value={String(currInv)} delta={invDelta} color={blue} />
        </Box>

        {/* 3 */}
        <Box compact={compact} sparkline={sparkVatDue?.length ? <Sparkline data={sparkVatDue} color={greenOrRed(vatDueDelta ?? 0)} height={compact ? 28 : 32} /> : null}>
          <TitleRow compact={compact} icon={<Calculator size={compact ? 12 : 14} />} label="VAT należny" />
          <ValueRow compact={compact} value={fmtPLN0(vatDueCurr)} delta={vatDueDelta} color={greenOrRed(vatDueDelta ?? 0)} />
          <div style={{ fontSize: compact ? 11 : 12, color: "#94a3b8" }}>
            VAT naliczony: <strong>{fmtPLN0(vatInCurr)}</strong>
          </div>
        </Box>

        {/* 4 — NEW card: Klienci */}
        <Box compact={compact}>
          <TitleRow compact={compact} icon={<ActivitySquare size={compact ? 12 : 14} />} label="Klienci (bieżący miesiąc)" />
          <ValueRow
            compact={compact}
            value={String(currClients)}
            delta={clientsDelta}
            color={blue}
          />
        </Box>

        {/* 5 (previously 4): Nowi kontrahenci */}
        <Box compact={compact}>
          <TitleRow compact={compact} icon={<Users size={compact ? 12 : 14} />} label="Nowi kontrahenci" hintColor={blue} />
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <div style={{ fontSize: compact ? 20 : 22, fontWeight: 800, color: blue }}>{String(newCustomers)}</div>
            <div style={{ fontSize: compact ? 11 : 12, color: "#94a3b8" }}>+ vs poprzedni m-c</div>
          </div>
        </Box>

        {/* 6 */}
        <Box compact={compact}>
          <TitleRow compact={compact} icon={<PieChart size={compact ? 12 : 14} />} label="Udział PL/EU/Import" />
          <div style={{ display: "flex", gap: 10, fontSize: compact ? 16 : 18, fontWeight: 800 }}>
            <span style={{ color: black }}>{pct(mix.pl)}</span>
            <span style={{ color: blue }}>{pct(mix.eu)}</span>
            <span style={{ color: "#dc2626" }}>{pct(mix.exp)}</span>
          </div>
          <div style={{ fontSize: compact ? 11 : 12, color: "#94a3b8" }}>wg pochodzenia</div>
        </Box>

        {/* 7 */}
        <Box compact={compact}>
          <TitleRow compact={compact} icon={<Percent size={compact ? 12 : 14} />} label="Wskaźnik koncentracji Top3" />
          <div style={{ fontSize: compact ? 20 : 22, fontWeight: 800, color: black }}>{pct(top3)}</div>
          <div style={{ fontSize: compact ? 11 : 12, color: "#94a3b8" }}>udział Top3 klientów</div>
        </Box>
      </div>

      {/* Footer */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#6b7280", marginTop: compact ? 0 : 2 }}>
        <FileText size={compact ? 14 : 16} />
        <span style={{ fontSize: compact ? 11 : 12 }}>{asOfText}</span>
      </div>
    </div>
  );
};

export default SprzedazKPI;
